/** How far into the video the poster is taken (the very first frame is often black). */
const POSTER_AT = 0.1;
const POSTER_MAX_SIDE = 1280;
/** A browser that never answers (a phone saving data, say) must not leave the loader spinning for good. */
const READ_TIMEOUT_MS = 10000;

/**
 * A video file's duration (seconds) and a frame near its start as a JPEG, read in the browser. Rejects when the
 * browser can't play it.
 */
export const readVideo = (file: File) =>
  new Promise<{ duration: number; poster: Blob }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const done = (error?: unknown, result?: { duration: number; poster: Blob }) => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      return error || !result ? reject(error ?? new Error("unreadable")) : resolve(result);
    };
    const timeout = setTimeout(() => done(new Error("unreadable")), READ_TIMEOUT_MS);
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.onerror = () => done(new Error("unreadable"));
    video.onloadedmetadata = () => {
      // a recording with no end written (a WebM from MediaRecorder) says Infinity: its length can't be told
      if (!Number.isFinite(video.duration)) {
        return done(new Error("unreadable"));
      }
      video.currentTime = Math.min(POSTER_AT, video.duration / 2 || 0);
    };
    video.onseeked = () => {
      const scale = Math.min(1, POSTER_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        poster =>
          done(poster ? undefined : new Error("unreadable"), poster ? { duration: video.duration, poster } : undefined),
        "image/jpeg",
        0.8
      );
    };
    video.src = url;
  });
